import { useState, useRef, useLayoutEffect } from "react"
import { isBefore, isAfter, isSameDay } from "date-fns"
import cn from "@/v2-ui/utils/utils.cn"
import {
  DATE_INPUT_PLACEHOLDER,
  formatDateInput,
  tryParseDate,
} from "./dateRangePicker.utils"

function formatDateMask(digits: string): string {
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`
}

export type DateRangePickerInputProps = {
  label?: string
  date: Date | undefined
  minDate?: Date
  maxDate?: Date
  active?: boolean
  onDateChange(date: Date): void
  onFocus?: () => void
}

function DateRangePickerInput(props: DateRangePickerInputProps) {
  const { label, date, minDate, maxDate, active, onDateChange, onFocus } = props
  const inputRef = useRef<HTMLInputElement>(null)
  const cursorRef = useRef<number | null>(null)

  const [text, setText] = useState(() => formatDateInput(date))
  const [prevTime, setPrevTime] = useState(() => date?.getTime())

  const dateTime = date?.getTime()
  if (dateTime !== prevTime) {
    setPrevTime(dateTime)
    setText(formatDateInput(date))
  }

  useLayoutEffect(() => {
    if (cursorRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorRef.current, cursorRef.current)
      cursorRef.current = null
    }
  })

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target
    const raw = input.value
    const cursor = input.selectionStart ?? raw.length

    const digitsBeforeCursor = raw.slice(0, cursor).replaceAll(/\D/g, "").length
    const digits = raw.replaceAll(/\D/g, "").slice(0, 8)
    const formatted = formatDateMask(digits)

    let newCursor = 0
    let counted = 0
    for (let i = 0; i < formatted.length && counted < digitsBeforeCursor; i++) {
      newCursor = i + 1
      if (formatted[i] !== ".") counted++
    }

    cursorRef.current = newCursor
    setText(formatted)
  }

  function handleCommit() {
    const parsed = tryParseDate(text)
    if (!parsed) {
      setText(formatDateInput(date))
      return
    }
    if (minDate && isBefore(parsed, minDate)) {
      setText(formatDateInput(date))
      return
    }
    if (maxDate && isAfter(parsed, maxDate)) {
      setText(formatDateInput(date))
      return
    }
    if (date && isSameDay(parsed, date)) return
    onDateChange(parsed)
  }

  return (
    <div
      className={cn(
        "ui-date-picker-input-group",
        active && "ui-date-picker-input-group-active",
      )}
    >
      {label && <label className="ui-date-picker-input-label">{label}</label>}
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        maxLength={10}
        className="ui-date-picker-date-input"
        placeholder={DATE_INPUT_PLACEHOLDER}
        value={text}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={handleCommit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            handleCommit()
          }
        }}
      />
    </div>
  )
}

export default DateRangePickerInput
